// Import React hooks
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";

// Import WaveSurfer
import AudioController from "./AudioController";
import {GlobalContext} from "../../../../context/GlobalContext";
import Spinner from "../../../loaders/Spinner";
import useContainerSize from "../../../../hooks/useContainerSize";
import WaveSurfer from "wavesurfer.js";

interface Props {
    container_id: string;
    url: string;
    play?: boolean;
    repeat?: boolean;
    mic?: boolean;
    width?: string;
    height?: number;
    surferRef?: any;
    social?: React.ReactNode;
    showTime?: boolean;
    showVolume?: boolean;
    mainLayout?: boolean;
    progressStyle?: boolean;
    setMetronomeIsPlaying?: (value?: boolean) => Promise<any>;
}

const WaveForm = ({

                       surferRef,
                       url,
                       play = true,
                       repeat = false,
                       mic = false,
                       width = '36vw',
                       height = 40,
                       social,
                       mainLayout,
                       showTime = true,
                      showVolume = false,
                       progressStyle = false,
                   }: Props) => {
    const {
        currentTime,
        volume,
        setVolume,
    } = useContext(GlobalContext);
    const [waveSurfer, setWaveSurfer] = useState<WaveSurfer>({} as any);
    const [isWaveformReady, setIsWaveformReady] = useState(false);
    const [isWaveformLoading, setIsWaveformLoading] = useState(true);
    const [waveformProgress, setWaveformProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false)

    const waveformContainerRef = useRef<WaveSurfer>(null)

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const {isSmall} = useContainerSize(wrapperRef)

    useEffect(() => {
        if (!waveformContainerRef || !url) {
            console.log('Container or URL is not defined yet.');
            return;
        }

        // if (waveSurfer) {
        //     waveSurfer.destroy();  // Destroy any existing WaveSurfer instance
        // }
       const instance = WaveSurfer.create({
            container: waveformContainerRef.current as unknown as HTMLElement,
            backend: 'MediaElement',
            waveColor: [ // an array of colors, to be applied as gradient color stops to the waveform.
                "#B3B3B3",
                "#282828",
                "gray",
                "lightgray",
                "rgba(0,255,255,.2)",
            ],
            progressColor: 'orange',
            barWidth: 2,
            barRadius: 1,
            dragToSeek: true,
            width: width,
            height: height,
            cursorColor: 'orange',
            cursorWidth: 2,
            url: url,
        });

        // Check loading progress
        instance.on('loading', (progress: any) => {
            setWaveformProgress(progress)
            if (progress === 100) {
                setIsWaveformLoading(false)
            }
        })

        // Once the waveform is ready, set the state to true
        instance.on('ready', () => {
            setIsWaveformReady(true)
        })

        setWaveSurfer(instance);

        return () => {
            instance.destroy();
        }
    }, [height, surferRef, url, width]);


    const handleStop = useCallback(() => {
        if (waveSurfer.isPlaying()) {
            return waveSurfer.stop()
        }
    }, [waveSurfer])

    // Handle the play pause button
    const handlePlayPause = useCallback(() => {
        if (!waveSurfer) {
            return;
        }

        if (!isWaveformReady) {
            return;
        }
        if (waveSurfer.isPlaying()) {
            setIsPlaying(false) // set the state to false
            waveSurfer.pause()
        } else {
            setIsPlaying(true)  // set the state to true
            waveSurfer.play()
        }

    }, [waveSurfer, isWaveformReady])

    // Handle Fast Forward
    const handleFastForward = useCallback(() => {
        if (!waveSurfer) {
            return;
        }

        if (!isWaveformReady) {
            return;
        }
        waveSurfer.setTime(waveSurfer.getCurrentTime() + 5)
    }, [isWaveformReady, waveSurfer])

    // Handle Fast Rewind
    const handleFastRewind = useCallback(() => {
        if (!waveSurfer) {
            return;
        }

        if (!isWaveformReady) {
            return;
        }
        waveSurfer.setTime(waveSurfer.getCurrentTime() - 5)
    }, [isWaveformReady, waveSurfer])


    // Render the wavesurfer component
    // and a button to load a different audio file
    return (
        <div className={`${mainLayout ? 'flex' : 'flex flex-col lg:flex-row'} lg:flex lg:flex-row`} ref={wrapperRef}>
            {/*TODO: Put Skeleton Loader here*/}
            <div
                className={`items-start mt-6 sm:mt-0 sm:md-0 ${progressStyle ?? 'w-0 h-0'} ${social ? 'bg-neutral-900' : 'bg-neutral-900'} px-3 py-2 rounded-full min-w-[${width}] relative`}
                // id={container_id}
                ref={waveformContainerRef as unknown as React.RefObject<HTMLDivElement>}
            >
                {
                    isWaveformLoading && (
                        <Spinner progress={waveformProgress}/>
                    )
                }
            </div>
            {/*</PlayerPortal>*/}

            {!progressStyle  &&  <div className={`flex justify-center items-center ${mainLayout ? 'mt-0' : 'mt-6'} sm:mt-0 gap-4 sm:gap-0`}>
                <AudioController handlePlayPause={handlePlayPause}
                                 handleFastForward={handleFastForward}
                                 handleFastRewind={handleFastRewind}
                                 handleStop={handleStop}
                                 isPlaying={isPlaying}
                                 currentTime={currentTime}
                                 volume={volume}
                                 setVolume={setVolume}
                                 small={isSmall}
                                 showTime={showTime}
                                 showVolume={showVolume}
                                 ended={false} play={play} repeat={repeat} mic={mic}/>

                {social}
            </div>}

        </div>
    )
}

export default WaveForm;
